import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { iff } from "@bryxinc/ts-utils";
import { styled } from "@mui/material/styles";
import { DataGridPremium as DataGrid } from "@mui/x-data-grid-premium";
import { forwardRef } from "react";
import BryxColors from "../../types/theme";
import BryxBox from "../BryxBox";
import { BryxCheckbox } from "../BryxCheckbox";
import BryxIcon from "../BryxIcon/BryxIcon";
import { BryxDataGridColumnMenu } from "./BryxDataGridColumnMenu";
import BryxDataGridFilterInputDate from "./BryxDataGridFilterInputDate";
import BryxDataGridFilterInputValue from "./BryxDataGridFilterInputValue";
import { BryxDataGridFilterPanel } from "./BryxDataGridFilterPanel";
import BryxDataGridFooter from "./BryxDataGridFooter";
import BryxDataGridToolbar from "./BryxDataGridToolbar";
import BryxGridFilterInputSingleSelect from "./BryxGridFilterInputSingleSelect";
// Comes from the allowed values of the `operator` attribute on `filter`
// https://bryx.stoplight.io/docs/bryx/branches/main/swg7gf41k2u1d-query-parameters
export const ALLOWED_FILTER_OPERATORS = [
    "equals",
    "notEqual",
    "greaterThan",
    "lessThan",
    "greaterThanOrEqual",
    "lessThanOrEqual",
    "contains",
];
const columnTypeFilterMap = {
    string: ["equals", "notEqual", "contains"],
    number: ["equals", "notEqual", "greaterThan", "lessThan", "greaterThanOrEqual", "lessThanOrEqual"],
    date: ["equals", "notEqual", "greaterThan", "lessThan", "greaterThanOrEqual", "lessThanOrEqual", "contains"],
    boolean: ["equals"],
};
const columnTypeInputComponentMap = {
    string: BryxDataGridFilterInputValue,
    number: BryxDataGridFilterInputValue,
    date: BryxDataGridFilterInputDate,
    boolean: BryxGridFilterInputSingleSelect,
};
const getGridFiltersForColumnType = (columnType, hasValueOptions) => columnTypeFilterMap[columnType].map((operator) => ({
    value: operator,
    InputComponent: hasValueOptions ? BryxGridFilterInputSingleSelect : columnTypeInputComponentMap[columnType],
    InputComponentProps: { type: columnType },
    // Filtering is done server side. Don't care about frontend in-memory filtering
    getApplyFilterFn: () => null,
}));
export const BryxDataGrid = styled(({ components, onBulkArchive, onBulkExport, columns, disableDragIcon, componentsProps, ...rest }) => (_jsx(BryxBox, { isFlex: true, fullHeight: true, fullWidth: true, children: _jsx(BryxBox, { sx: { flexGrow: 1 }, children: _jsx(DataGrid, { disableSelectionOnClick: true, pagination: true, paginationMode: "server", headerHeight: 51, rowHeight: 51, autoHeight: true, localeText: {
                columnMenuSortAsc: "Sort by Ascending",
                columnMenuSortDesc: "Sort by Descending",
                columnMenuHideColumn: "Hide Column",
                filterPanelColumns: "Column Name",
                filterPanelOperators: "Filter Type",
                filterPanelInputLabel: "Value",
                filterPanelAddFilter: "Add Filter",
                // only custom filterOperators
                filterOperatorEquals: "Equals",
                filterOperatorNotEqual: "Not Equal",
                filterOperatorGreaterThan: "Greater Than",
                filterOperatorLessThan: "Less Than",
                filterOperatorGreaterThanOrEqual: "Greater Than or Equal",
                filterOperatorLessThanOrEqual: "Less Than or Equal",
                filterOperatorContains: "Contains",
            }, components: {
                BaseCheckbox: forwardRef((props, _) => _jsx(BryxCheckbox, { compact: true, ...props })),
                ColumnMenu: BryxDataGridColumnMenu,
                FilterPanel: BryxDataGridFilterPanel,
                Footer: BryxDataGridFooter,
                Toolbar: BryxDataGridToolbar,
                ...components,
            }, componentsProps: {
                ...componentsProps,
                toolbar: {
                    ...componentsProps?.toolbar,
                    onBulkArchive,
                    onBulkExport,
                },
            }, ...rest, columns: columns.map((c) => ({
                ...c,
                valueOptions: c.valueOptions
                    ? c.valueOptions
                    : iff(c.type === "boolean", [
                        { label: "True", value: true },
                        { label: "False", value: false },
                    ]),
                renderHeader: (params) => (_jsxs("div", { style: {
                        // Make column headers bold
                        fontWeight: 700,
                        // Fix overflow
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        lineHeight: 1.5,
                    }, children: [!disableDragIcon && _jsx(BryxIcon, { iconName: "drag", smIconSize: true }), params.colDef.headerName] })),
                filterOperators: (() => {
                    if (!c.type || !Object.keys(columnTypeFilterMap).includes(c.type))
                        return undefined;
                    return getGridFiltersForColumnType(c.type, Boolean(c.valueOptions));
                })(),
            })) }) }) })))(({ theme, marginX }) => ({
    "&": { border: "none", marginRight: "23px" },
    "& .MuiDataGrid-main": {
        border: `1px solid ${BryxColors.specialColor(theme.palette.mode).greyButGray}`,
        borderRadius: "4px",
        marginLeft: marginX ? `${marginX}px` : "0px",
        marginRight: marginX ? `${marginX}px` : "0px",
        height: "100%",
    },
    "& .MuiDataGrid-virtualScroller": {
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "&::-webkit-scrollbar": { display: "none" },
    },
    "& .MuiDataGrid-iconSeparator": { display: "none !important" },
    "& .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within": {
        outline: "none !important",
    },
    "& .MuiDataGrid-cell": {
        border: "none",
        padding: "0px",
        paddingRight: "32px",
        paddingLeft: "8px",
    },
    "& .MuiDataGrid-menuIcon": {
        // Always show menu icon and make sure it is evenly spaced between columns
        // as per design (see .MuiDataGrid-columnHeaderDraggableContainer)
        visibility: "visible !important",
        width: "auto",
        flex: 1,
        "&:hover .MuiIconButton-root": {
            // Change from circle to rounded box for hovering menu button
            borderRadius: "4px",
        },
    },
    "& .MuiDataGrid-pinnedColumns": {
        marginRight: "8px",
    },
    "& .MuiDataGrid-pinnedColumns, & .MuiDataGrid-pinnedColumnHeaders": {
        boxShadow: "none !important",
        "& .MuiDataGrid-row": {
            marginLeft: "0 !important",
            "& .MuiDataGrid-cell:last-child": {
                paddingRight: "unset !important",
            },
        },
    },
    "& .MuiDataGrid-actionsCell .MuiSvgIcon-root": {
        fill: BryxColors.notify(theme.palette.mode, "inactive"),
        "&:focus": {
            outline: "none !important",
        },
        "&:hover": {
            fill: BryxColors.brand1(theme.palette.mode),
            cursor: "pointer",
        },
    },
    "& .MuiDataGrid-columnHeaders": {
        marginLeft: "24px",
        marginRight: "24px",
        borderBottom: `1px solid ${BryxColors.specialColor(theme.palette.mode).greyButGray}`,
    },
    "& .MuiDataGrid-columnHeader": {
        marginLeft: "-1px",
        "&:focus, &:focus-within": {
            // Disable click select border for column headers
            outline: "none !important",
        },
        // Show draggable indicator icon when hovered
        "&:hover, &.Mui-hovered": {
            "& .MuiDataGrid-columnHeaderDraggableContainer .MuiDataGrid-columnHeaderTitleContainerContent .MuiSvgIcon-root": {
                display: "inline-block !important",
                color: BryxColors.specialColor(theme.palette.mode).greyButGray,
            },
        },
        "& .MuiDataGrid-columnHeaderTitle": {
            // Make column headers bold
            fontWeight: 700,
            // Fix overflow
            textOverflow: "ellipsis",
            whiteSpace: "break-spaces",
            lineHeight: 1.5,
        },
        "& .MuiDataGrid-columnHeaderDraggableContainer": {
            // Make sure column menu button is evenly spaced between columns as per design
            flexGrow: 1,
            // Hide draggable indicator icon when not hovered and position it nicely
            "& .MuiDataGrid-columnHeaderTitleContainerContent :not(.MuiCheckbox-root) .MuiSvgIcon-root": {
                verticalAlign: "middle",
                marginBottom: "1px",
                left: "-10px",
                position: "absolute",
                top: "18px",
                display: "none",
            },
        },
    },
    // Pinned columns specifically
    "& .MuiDataGrid-pinnedColumnHeaders .MuiDataGrid-columnHeader": {
        // Disable the custom drag icon
        "& .MuiDataGrid-columnHeaderDraggableContainer .MuiDataGrid-columnHeaderTitleContainerContent .MuiSvgIcon-root": {
            display: "none !important",
        },
    },
    "& .MuiDataGrid-row": {
        borderTop: `1px solid ${BryxColors.specialColor(theme.palette.mode).greyButGray}`,
        marginLeft: "24px",
        marginRight: "24px",
        backgroundColor: "unset !important",
        "&:nth-of-type(1)": {
            borderTop: "none !important",
        },
        // Hover state effect
        // NB: must also match on .Mui-hovered to affect pinned columns in the virtualized grid
        "&:hover, &.Mui-hovered": {
            backgroundColor: "unset !important",
            "& .MuiDataGrid-cell .MuiDataGrid-cellContent": {
                color: BryxColors.brand1(theme.palette.mode),
                fontWeight: 700,
            },
            "& .MuiDataGrid-cell .MuiCheckbox-root": {
                border: `1px solid ${BryxColors.brand1(theme.palette.mode)}`,
            },
            "& .MuiDataGrid-cell svg": {
                fill: BryxColors.brand1(theme.palette.mode),
            },
            "& .MuiDataGrid-cell": {
                transform: "translateX(2px)",
            },
            "& .MuiDataGrid-treeDataGroupingCell": {
                color: BryxColors.brand1(theme.palette.mode),
                fontWeight: 700,
                transform: "translateX(2px)",
            },
        },
    },
    "& .MuiDataGrid-footerContainer": {
        marginRight: "24px",
        marginLeft: "24px",
        borderTop: "none !important",
        fontSize: "1rem",
        borderBottom: "none",
        paddingTop: "48px",
    },
}));
export default BryxDataGrid;
